<template>
    <transition name="fade">
        <component
            :is="tag"
            v-show="show"
            ref="popup"
            class="shadow-default z-index--10"
            :class="
                mode === 'tooltip'
                    ? 'bg--black--70 rounded-sm'
                    : 'bg-light rounded'
            "
        >
            <slot />

            <span
                v-if="arrow"
                class="popper__arrow"
                x-arrow=""
            />
        </component>
    </transition>
</template>

<script>
import Popper from "popper.js"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"
import { findDomReference } from "~/lib/vueUtils"

export default {
    props: {
        show: Boolean,
        name: String,
        placement: {
            type: String,
            default: "bottom-end",
        },

        fixed: {
            type: Boolean,
            default: false,
        },

        tag: {
            type: String,
            default: "div",
        },

        separation: {
            type: Number,
            default: 10, // px
        },

        slide: {
            type: Number,
            default: 0, // px
        },

        arrow: {
            type: Boolean,
            default: true,
        },

        flip: {
            type: Boolean,
            default: true,
        },

        mode: {
            type: String,
            default: "normal",
            validator: val => ["tooltip", "normal"].includes(val),
        },
    },

    emits: ["hide"],

    setup() {
        const { listen } = useListenerManager()
        return { listen }
    },

    data() {
        return {
            popper: null,
        }
    },

    watch: {
        show() {
            this.update()
        },
        arrow() {
            this.update()
        },
        placement() {
            this.update()
        },
        slide() {
            this.update()
        },
        separation() {
            this.update()
        },
    },

    mounted() {
        if (this.show) {
            this.update()
        }
    },

    beforeUnmount() {
        if (this.popper) {
            this.popper.destroy()
        }
    },

    methods: {
        click(event) {
            if (!this.show) return

            const popup = this.$refs.popup
            if (!popup) return

            const tgt = event.target

            const ignore = popup == tgt || popup.contains(tgt)
            if (!ignore) {
                this.$emit("hide", event)
            }
        },

        create() {
            const popup = this.$refs.popup
            const ref = this.findDomReference(this.name)
            if (!ref) throw new Error(`Couldn't find reference: ${this.name}`)

            this.popper = new Popper(ref, popup, {
                placement: this.placement,
                modifiers: {
                    offset: {
                        offset: `${this.slide}, ${this.separation}`,
                    },

                    flip: {
                        enabled: this.flip,
                    },
                },

                positionFixed: this.fixed,
                eventsEnabled: true,
            })
        },

        findDomReference(name) {
            return findDomReference(name, this)
        },

        update() {
            if (this.show) {
                if (!this.popper) this.create()
                this.popper.options.placement = this.placement
                const offsetModifier = this.popper.modifiers.find(
                    m => m.name == "offset"
                )
                offsetModifier.offset = `${this.slide}, ${this.separation}`
                const flipModifier = this.popper.modifiers.find(
                    m => m.name == "flip"
                )
                flipModifier.enabled = this.flip
                this.popper.enableEventListeners()
                this.popper.scheduleUpdate()
                this._removeClick = this.listen(
                    "click",
                    event => {
                        this.click(event)
                    },
                    true
                )
            } else {
                this._removeClick && this._removeClick()
                if (this.popper) {
                    this.popper.disableEventListeners()
                }
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "~/scss/bootstrap-importable";

$border-popup: 5px;

.popper__arrow {
    border-color: $light;
    border-style: solid;
    height: 0;
    margin: $border-popup;
    position: absolute;
    width: 0;
}

[x-placement^="top"] .popper__arrow {
    border-width: $border-popup $border-popup 0 $border-popup;
    border-left-color: transparent;
    border-right-color: transparent;
    border-bottom-color: transparent;
    bottom: -$border-popup;
    left: calc(50% - #{$border-popup});
    margin-top: 0;
    margin-bottom: 0;
}

[x-placement^="bottom"] .popper__arrow {
    border-width: 0 $border-popup $border-popup $border-popup;
    border-left-color: transparent;
    border-right-color: transparent;
    border-top-color: transparent;
    top: -$border-popup;
    left: calc(50% - #{$border-popup});
    margin-top: 0;
    margin-bottom: 0;
}

[x-placement^="right"] .popper__arrow {
    border-width: $border-popup $border-popup $border-popup 0;
    border-left-color: transparent;
    border-top-color: transparent;
    border-bottom-color: transparent;
    left: -$border-popup;
    top: calc(50% - #{$border-popup});
    margin-left: 0;
    margin-right: 0;
}

[x-placement^="left"] .popper__arrow {
    border-width: $border-popup 0 $border-popup $border-popup;
    border-top-color: transparent;
    border-right-color: transparent;
    border-bottom-color: transparent;
    right: -$border-popup;
    top: calc(50% - #{$border-popup});
    margin-left: 0;
    margin-right: 0;
}
</style>
